import { CSSProperties, useState } from "react";
import useCampaignBuilder, { CampaignExistsState, Caption } from "Hooks/useCampaignBuilder";
import { clsx, makeKey } from "modules/Utils";
import CardSymphony from "elements/CardSymphony";
import Colors from "modules/Colors";
import ButtonSymphony from "elements/ButtonSymphony";
import InputSymphony from "elements/InputSymphony";
import Icon from "elements/Icon";
import { PlaybookType } from "modules/Const";
import { InstagramPost } from "modules/Types";
import { RecordDetails } from "modules/Types";
import { CloudinaryUploadedFileType } from "./CloudinaryUploadWidget";
import Axios from "helpers/Interceptor";
import useBrand, { BrandExistsState } from "Hooks/useBrand";
import { MaestroLoadingMessage } from "pages/MarketingPage/MaestroBanner";
import Banner from "elements/Banner";

interface AdCaptionsSelectorProps {
    className: string;
    style?: CSSProperties;
}

const generateCaptionsForAds_api = async (data: {
    selectedSongToPromote: RecordDetails | null,
    selectedInstagramPosts: InstagramPost[],
    uploadedAssets: CloudinaryUploadedFileType[],
    captions: Caption[],
    playbookType: PlaybookType,
    brandId: number,
}): Promise<{
    text: string,
    reason: string,
}[]> => {
    const res = await Axios.post(`/agents/generate-captions-for-ads`, {
        selectedSongToPromote: data.selectedSongToPromote,
        selectedInstagramPosts: data.selectedInstagramPosts,
        uploadedAssets: data.uploadedAssets,
        captions: data.captions,
        playbookType: data.playbookType,
        brandId: data.brandId,
    });
    return res.data;
}

const AdCaptionsSelector = ({
    className,
    style
}: AdCaptionsSelectorProps) => {
    const {
        selectedSongToPromote,
        selectedInstagramPosts,
        uploadedAssets,
        captions,
        setCaptions,
        playbookType,
    } = useCampaignBuilder() as CampaignExistsState;

    const {
        currentBrand
    } = useBrand() as BrandExistsState

    const [writeIdeaCaption, setWriteIdeaCaption] = useState<string>('');
    const [generatingCaptionsLoading, setGeneratingCaptionsLoading] = useState<boolean>(false);

    const userCanAddCaptions = uploadedAssets.length > 0
    const userCanAddMultipleCaptions = selectedInstagramPosts.length === 0

    if (!userCanAddCaptions) {
        return null;
    }

    const handleGenerateCaption = async () => {
        try {
            setGeneratingCaptionsLoading(true);
            const generatedCaptions = await generateCaptionsForAds_api({
                selectedSongToPromote: selectedSongToPromote || null,
                selectedInstagramPosts,
                uploadedAssets,
                captions,
                playbookType,
                brandId: currentBrand.id,
            });
            const transformedCaptions = generatedCaptions.map((caption, index) => ({
                id: captions.length + index + 1,
                text: caption.text,
                aiGenerated: true,
            }));
            setCaptions(transformedCaptions);
        } catch (e) {
            console.error(e);
        } finally {
            setGeneratingCaptionsLoading(false);
        }
    }

    const handleGenerateCaptionFromModal = () => {
        console.log('generate caption from modal');
    }

    const handleCaptionChange = (value: string, captionId: number) => {
        const newCaptions = captions.map(caption =>
            caption.id === captionId ?
                { ...caption, text: value }
                :
                caption
        );
        setCaptions(newCaptions);
    }

    const handleAddCaption = () => {
        setCaptions([...captions, { id: captions.length, text: '' }]);
    }

    const handleRemoveCaption = (index: number) => {
        const newCaptions = [...captions];
        newCaptions.splice(index, 1);
        setCaptions(newCaptions);
    }

    const renderGenerateCaptionButton = () => {
        if (!userCanAddMultipleCaptions) {
            return null;
        }

        return (
            <div
                className="adCaptionsSelectorCampaignInner"
                style={sty.adCaptionsSelectorCampaignInner}
            >
                <div
                    className="adCaptionsSelectorCampaignInnerTitle"
                    style={{ marginBottom: 8 }}
                >
                    Let Maestro craft the perfect captions for your ad based on your selected content.
                </div>
                <ButtonSymphony
                    isLoading={generatingCaptionsLoading}
                    className="adCaptionsSelectorCampaignInnerButton"
                    onClick={handleGenerateCaption}
                >
                    Generate Captions
                </ButtonSymphony>
                {generatingCaptionsLoading && <MaestroLoadingMessage startLoading={true} />}
            </div>
        )
    }

    const renderCaptionList = () => {
        return (
            <div
                className="captionsListContainer"
                style={sty.captionsListContainer}
            >
                {captions.map((caption, index) => {
                    let captionText = caption.text;
                    let captionId = caption.id;
                    let showActionBar = userCanAddMultipleCaptions && captions.length > 1;
                    return (
                        <div
                            key={makeKey('caption', index)}
                            className="captionItem"
                        >
                            <InputSymphony
                                isTextArea
                                rows={2}
                                className="inputCaptionAd"
                                value={captionText}
                                onChange={(value: string) => handleCaptionChange(value, captionId)}
                                placeholder="Type your caption here"
                                styleInput={sty.inputCaptionAd}
                                style={{ borderColor: Colors.greyLight }}
                            />
                            {showActionBar && <div
                                className="writeIdeaButtonContainer"
                                style={sty.writeIdeaButtonContainer}
                            >
                                {/* <ButtonSymphony
                                    className="captionItemRemoveButtonButton"
                                    onClick={() => {
                                        setShowWriteIdeaModal(true);
                                        setWriteIdeaCaption(captionText);
                                    }}
                                    iconLeft="maestroBadge"
                                    variant="text"
                                >
                                    Write an idea
                                </ButtonSymphony> */}
                                {captions.length > 1 &&
                                    <Icon
                                        className="captionItemRemoveButton"
                                        icon="trash"
                                        onClick={() => handleRemoveCaption(index)}
                                        style={{
                                            marginLeft: 'auto'
                                        }}
                                    />
                                }
                            </div>}
                        </div>
                    )
                })}
                {false && captions.length < 5 && (<ButtonSymphony
                    className="addCaptionButton"
                    onClick={handleAddCaption}
                    variant="text"
                >
                    Add new caption
                </ButtonSymphony>)}
            </div>
        )
    }

    let showPlatformPostDisclaimer = selectedInstagramPosts.length > 0;

    let captionsSubtitle = 'Add a caption for your uploaded videos.'
    if (userCanAddMultipleCaptions) {
        captionsSubtitle = 'Add up to 5 captions to test with your ads. We\'ll test posts & captions to find what\'s working best.'
    }

    return (
        <>
            <CardSymphony
                className={clsx("adCaptionsSelectorCampaignComponent", className)}
                style={style}
                title="Captions"
                subtitle={captionsSubtitle}
                size="small"
            >
                {showPlatformPostDisclaimer &&
                    <Banner className="platformPostDisclaimer" color="yellow">
                        <div className="platformPostDisclaimerText" style={sty.text16bold}>
                            Your Instagram posts will use their post captions. The captions below will be applied to your uploaded assets.
                        </div>
                    </Banner>
                }
                {renderCaptionList()}
                {renderGenerateCaptionButton()}
            </CardSymphony>

            {/* MODALS */}
            {/* <ModalSymphony
                className="writeIdeaModal"
                isOpen={showWriteIdeaModal}
                title="Maestro AI will help you"
                subtitle="What do you want to communicate?"
                headerIcon="maestroBadgeImage"
                onClose={() => setShowWriteIdeaModal(false)}
                style={{
                    gap: 16,
                }}
            >
                <InputSymphony
                    className="writeIdeaModalInput"
                    value={writeIdeaCaption}
                    onChange={(value: string) => setWriteIdeaCaption(value)}
                    placeholder="Type your caption here"
                />
                <ButtonSymphony
                    className="writeIdeaModalButton"
                    onClick={handleGenerateCaptionFromModal}
                >
                    Generate
                </ButtonSymphony>
            </ModalSymphony> */}
        </>
    )
}

const sty: Record<string, CSSProperties> = {
    adCaptionsSelectorCampaignInner: {
        width: '100%',
        backgroundColor: Colors.purpleLightest,
        padding: 16,
        borderRadius: 8,
    },
    captionsListContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    writeIdeaButtonContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        backgroundColor: Colors.greyLight,
        padding: 8,
        borderBottomLeftRadius: 8,
        borderBottomRightRadius: 8,
    },
    inputCaptionAd: {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
    }
}

export default AdCaptionsSelector;